import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../../components/useBreadcrumbBack";
import { useAuth } from "../../../../providers/AuthProvider";
import { ApiError } from "../../../../services/apiClient";
import { listPersonalActivity } from "../../../shared/api/auditLogs";
import { useApiQuery } from "../../../../hooks/useApiQuery";

import { PageShell } from "../../../shared/layouts/PageShell";
import { PageHeader } from "../../../shared/ui/layouts/PageHeader";
import { defaultIconProps } from "../../../shared/ui/icons";

import {
  ArrowLeft,
  ArrowsClockwise,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";

const PAGE_SIZE = 25;

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const heroContentClasses = "max-w-3xl";
const heroTitleClasses = "text-4xl font-semibold text-slate-900 dark:text-slate-100";
const actionBarClasses = "flex flex-wrap gap-3";
const refreshButtonClasses =
  "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";
const ghostButtonClasses =
  "inline-flex items-center gap-2 rounded-md border border-transparent bg-transparent px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 disabled:opacity-60 dark:text-slate-400 dark:hover:bg-slate-800";
const tableWrapperClasses =
  "relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900";
const tableOverlayClasses =
  "absolute inset-0 flex items-center justify-center bg-slate-100/70 text-sm font-semibold text-slate-600 dark:bg-slate-900/70 dark:text-slate-400";
const emptyStateClasses =
  "flex flex-col items-center gap-4 px-6 py-12 text-center text-slate-500 dark:text-slate-400";
const dataTableClasses =
  "min-w-full divide-y divide-slate-200 text-left text-sm text-slate-700 dark:divide-slate-800 dark:text-slate-300";
const tableHeaderCellClasses =
  "bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400";
const summaryCellClasses = "px-4 py-4 align-top";
const summaryTextClasses = "font-semibold text-slate-900 dark:text-slate-100";
const metaStackClasses =
  "mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-400";
const badgeClasses =
  "rounded-full border border-slate-200 bg-slate-50 px-3 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400";
const timestampCellClasses =
  "w-60 whitespace-nowrap px-4 py-4 text-sm text-slate-600 align-top dark:text-slate-400";
const skeletonBaseClasses =
  "h-4 w-full rounded bg-slate-200 motion-safe:animate-pulse motion-reduce:animate-none dark:bg-slate-700";
const skeletonShortClasses = "w-1/2";
const paginationCardClasses =
  "flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-600 shadow-xl dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400";
const paginationControlsClasses = "flex gap-3";


export function ProfileActivityPage() {
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();
  const [offset, setOffset] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("settings.profile.title"), to: "/settings/profile" },
      { label: t("settings.activity.title") },
    ];
  }, [t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/settings/profile" },
  });

  const dateFormatter = useMemo(() => {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, []);

  const formatDate = useCallback(
    (value: string) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return dateFormatter.format(date);
    },
    [dateFormatter],
  );

  const queryFn = useCallback(
    (token: string) =>
      listPersonalActivity(token, {
        limit: PAGE_SIZE,
        offset
      }),
    [offset]
  );

  const formatError = useCallback(
    (err: unknown) =>
      err instanceof ApiError ? err.message : t("settings.activity.loadError"),
    [t]
  );

  const { data, isLoading, error, refresh } = useApiQuery({
    queryFn,
    formatError
  });

  const activities = data?.activities ?? [];
  const totalCount = data?.total_count ?? 0;

  useEffect(() => {
    if (!isLoading && data) {
      setLastSyncedAt(Date.now());
    }
  }, [data, isLoading]);

  if (!user || !accessToken) {
    return null;
  }

  const hasPrevious = offset > 0;
  const hasNext = offset + activities.length < totalCount;
  const start = activities.length === 0 ? 0 : offset + 1;
  const end = activities.length === 0 ? 0 : offset + activities.length;

  const renderRows = () => {
    if (isLoading) {
      return Array.from({ length: 5 }).map((_, index) => (
        <tr key={`placeholder-${index}`}>
          <td className={summaryCellClasses}>
            <div className={skeletonBaseClasses} />
            <div className={`${skeletonBaseClasses} mt-2 ${skeletonShortClasses}`} />
          </td>
          <td className={timestampCellClasses}>
            <div className={`${skeletonBaseClasses} ${skeletonShortClasses}`} />
          </td>
        </tr>
      ));
    }

    return activities.map((entry) => (
      <tr key={entry.id}>
        <td className={summaryCellClasses}>
          <p className={summaryTextClasses}>{entry.summary}</p>
          <div className={metaStackClasses}>
            <span className={badgeClasses}>
              {entry.resource_type || t("common.table.empty_value")}
            </span>
            <code className="text-xs font-mono uppercase text-slate-500 dark:text-slate-400">
              {entry.action}
            </code>
          </div>
        </td>
        <td className={timestampCellClasses}>{formatDate(entry.occurred_at)}</td>
      </tr>
    ));
  };

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={t("settings.activity.title")}
        subtitle={t("settings.activity.subtitle")}
        description={
          lastSyncedAt
            ? t("settings.activity.lastSynced", {
                timestamp: formatDate(new Date(lastSyncedAt).toISOString()),
              })
            : t("settings.activity.lastSyncedPending")
        }
        contentClassName={heroContentClasses}
        titleClassName={heroTitleClasses}
        actions={
          <div className={actionBarClasses}>
            <button
              type="button"
              className={refreshButtonClasses}
              onClick={() => void refresh()}
              disabled={isLoading}
            >
              <ArrowsClockwise
                {...defaultIconProps}
                className={isLoading ? "profile-activity__icon-spin" : undefined}
                aria-hidden
              />
              {t("actions.refresh")}
            </button>
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      {error && (
        <div className="form-feedback form-feedback--error" role="alert">
          {error}
        </div>
      )}

      <div className={tableWrapperClasses}>
        {isLoading && (
          <div className={tableOverlayClasses} aria-live="polite">
            {t("settings.activity.loading")}
          </div>
        )}
        {!isLoading && activities.length === 0 ? (
          <div className={emptyStateClasses} role="status">
            <p>{t("settings.activity.emptyState")}</p>
            <button
              type="button"
              className={refreshButtonClasses}
              onClick={() => void refresh()}
            >
              {t("actions.refresh")}
            </button>
          </div>
        ) : (
          <table className={dataTableClasses}>
            <thead>
              <tr>
                <th scope="col" className={tableHeaderCellClasses}>
                  {t("settings.activity.table.event")}
                </th>
                <th scope="col" className={tableHeaderCellClasses}>
                  {t("settings.activity.table.time")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">{renderRows()}</tbody>
          </table>
        )}
      </div>

      <div className={paginationCardClasses}>
        <div>
          {t("settings.activity.pagination.summary", {
            start,
            end,
            total: totalCount,
          })}
        </div>
        <div className={paginationControlsClasses}>
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
            disabled={!hasPrevious || isLoading}
          >
            <CaretLeft {...defaultIconProps} aria-hidden />
            {t("settings.activity.pagination.previous")}
          </button>
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={() => setOffset((current) => current + PAGE_SIZE)}
            disabled={!hasNext || isLoading}
          >
            {t("settings.activity.pagination.next")}
            <CaretRight {...defaultIconProps} aria-hidden />
          </button>
        </div>
      </div>
    </PageShell>
  );
}
